import { Registry } from '../Registry';
import { Template, TemplateContext } from './Template';




export interface ViewConfig<TData = {}> {
	data?:TData,
	template?:string|Element,
	registry?:ViewRegistry,
}


/**
 * A common view class that has a container to render content/template into.
 */
export class View<TData = any> {

	private _template:Template;

	constructor( config:ViewConfig<TData> = {} ) {

		this._template = new Template( config.template );

		if( config.registry ) this._template.context.registry = config.registry;
		if( config.data ) this._template.context.data = config.data;

		this._template.context.view = this;
	}


	/**
	 * Renders the view template.
	 * If an empty target is given, the template is rendered into to given target.
	 * If the target is not empty, the content of the target is used as the template.
	 */
	public render() {

		// do rendering of template
		this._template.render();

		// everything is ready now we can invoke view's init
		invokeInit( this );
	}

	/**
	 * The init function is automatically called after the render function.
	 * So everything is rendered and the node is ready to be used.
	 * Overwrite the init function for custom dom manipulations.
	 */
	public init() {

	}

	/**
	 * Destroys properly the view
	 */
	destroy() {
		this._template.destroy();
		invokeDeinit( this );
	}

	/**
	 * The deinit function is automatically called after the view is destroyed.
	 * Use it to unsubscribe signals etc
	 */
	public deinit() {

	}


	/**
	 * Returns the root node of the view
	 */
	get node():Element {
		return this._template.node;
	}

	get data():TData {
		return this._template.context.data as TData;
	}
	set data( d:TData ) {
		this._template.context.data = d;
	}

	get context() {
		return this._template.context;
	}
	get registry() {
		return this._template.context.registry;
	}
	get template() {
		return this._template;
	}
}


/**
 * A registry to lookup views
 */
export class ViewRegistry extends Registry<any,any,View> {}


/**
 * A factory function that creates a view based on the given key to lookup and its context
 */
export function create( key:any, context:TemplateContext ) {

	/**
	 * Lookups the viewName in the template context registry.
	 * Furthermore it lookups in all parent template contexts
	 */
	function lookup( key:any, context:TemplateContext ) {

		while( context ) {
			let found = context.registry.get( key );
			if( found ) return found;
			context = context.parent;
		}

		return null;
	}

	let factory = lookup( key, context );

	// no view found with name
	if( !factory ) throw new Error( "No registered view found with name: " + key );

	let view = factory( key );

	/// inject parent template in context
	view.template.extendContext( context );

	return view;
}


/**
 * Internal helper function that executes init functions on view & data
 */
export function invokeInit( v:View ) {
	if( v.data && typeof v.data[ 'init' ] == 'function' ) v.data.init();
	v.init();
}

/**
 * Internal helper function that executes init functions on view & data
 */
export function invokeDeinit( v:View ) {
	if( v.data && typeof v.data[ 'deinit' ] == 'function' ) v.data.deinit();
	v.deinit();
}
